import { useState } from "react";
import { getStrings } from "../i18n/strings";
import { hasScore, type PublicRisk, type PublicTorNetwork } from "../types/dto";
import { groupSignalsForWhyThisScore, signalLabel, signalStateLabel } from "../utils/riskSignals";
import {
  anonymityProfileLabel,
  keyFindingBody,
  keyFindingTitle,
  networkContextTypeLabel,
  primaryReasonBody,
  resolveNetworkContext,
  serviceFrictionBody,
  serviceFrictionLabel,
  severityTone,
  signalExplanationFor,
  summaryBody,
  summaryTitle,
} from "../utils/riskExplanation";

type Strings = ReturnType<typeof getStrings>;

// key-signals__dot's existing modifier vocabulary (safe/neutral/alert) predates severity tones — this
// maps severityTone()'s five-value output onto it, adding the two shades ("caution"/"elevated") that
// tone could never previously express (a keySignals entry only ever got the flat "alert" red dot).
const KEY_SIGNAL_DOT_TONE: Record<string, string> = {
  safe: "safe",
  neutral: "neutral",
  caution: "caution",
  elevated: "elevated",
  danger: "alert",
};

// A single curated signal's Detected/Not detected/Unknown/Unavailable row — the 4-state counterpart to
// ResultView's boolean-only TriStateField. "unknown" and "not_supported" each get their own muted
// visual treatment, distinct from both "detected" (alert) and "not_detected" (neutral/safe): collapsing
// either into "not detected" would silently misreport "we don't know" or "nothing we use even checks
// for this" as "checked, and it's clean".
//
// When the result carries a signalExplanations entry for this signal type (risk-v4.9's Network Context /
// Risk Explanation feature), a second modifier class layers the finer-grained severity tone (green/yellow/
// orange/red/gray) on top — e.g. a Detected AttackSource that was contextualized for a recognized public
// DNS resolver renders caution-yellow here instead of the flat "detected = red" it would otherwise get.
// The raw field--signal-{state} class/coloring stays untouched underneath for a snapshot that predates
// this feature and so never carries a severity.
export function SignalStateField({ signalType, state, strings, risk }: { signalType: string; state: string; strings: Strings; risk?: PublicRisk }) {
  const explanation = risk ? signalExplanationFor(risk, signalType) : undefined;
  const tone = explanation ? severityTone(explanation.severity) : null;
  const className = ["field", "field--status", `field--signal-${state}`, tone ? `field--severity-${tone}` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className}>
      <span className="field-label">{signalLabel(signalType, strings)}</span>
      <span className="field-value"><span className="status-dot" aria-hidden="true" />{signalStateLabel(state, strings)}</span>
    </div>
  );
}

// The public-infrastructure identity banner — shown only when the server reliably recognized this
// address's network function (spec: confidence gate already enforced server-side, never re-derived here).
// {organization} is this round's own live evidence text, never a hardcoded brand name — see
// NetworkContextResolver's docs on why the specific operator name is always dynamic.
export function NetworkContextBanner({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  const context = resolveNetworkContext(risk);
  if (!context.isRecognized) return null;

  const typeLabel = networkContextTypeLabel(context.type, strings);
  const titleTemplate = strings.networkContextBannerTitle ?? "Recognized public infrastructure: {type}";
  const bodyTemplate =
    strings.networkContextBannerBody ??
    "This address is recognized as {type}{organization}. Because this kind of infrastructure is commonly shared across many unrelated users and services, third-party reputation records showing scanning or abuse history are not unusual on their own. NetRiskScan has reduced the weight of such historical, shared-infrastructure-related records accordingly. No evidence here indicates an active, high-confidence threat.";

  const organizationSuffix = context.organization ? ` (${context.organization})` : "";

  return (
    <div className="network-context-banner" role="note">
      <strong>{titleTemplate.replace("{type}", typeLabel)}</strong>
      <p>{bodyTemplate.replace("{type}", typeLabel).replace("{organization}", organizationSuffix)}</p>
    </div>
  );
}

// The one-paragraph "why this score" headline/body, built server-side (RiskExplanationEngine) rather than
// pieced together from raw signal booleans in this component — see utils/riskExplanation.ts.
export function RiskSummaryPanel({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  if (!risk.summaryCode) return null;

  return (
    <div className="risk-summary-panel">
      <strong>{summaryTitle(risk.summaryCode, strings)}</strong>
      <p>{summaryBody(risk.summaryCode, strings)}</p>
    </div>
  );
}

// risk-v4.12 (spec §八/§十九): the two sentences that make a moderate Index legible.
//
// The reported failure mode was a page that could say "Index 1 / Critical" and nothing else. The half of
// that this panel fixes is the *why*: a commercial VPN exit with no abuse record now says, in so many
// words, that its score comes from the kind of network it is rather than from anything it has done — and
// then says separately that third-party services will still challenge it, which is the actual experience
// that prompted the report. Both lines come from server-issued codes (RiskExplanationDetail /
// ServiceFriction), never from re-reading raw signal booleans here.
//
// Service friction is deliberately words, not a number or a meter. NetRiskScan publishes exactly one
// 0-100 figure and has twice had that broken by a well-meant second one; three ordinal words cannot be
// rendered as a second ring.
export function RiskWhyPanel({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  const reason = primaryReasonBody(risk.explanation?.primaryReason, strings);
  const frictionLevel = serviceFrictionLabel(risk.serviceFriction, strings);
  const frictionBody = serviceFrictionBody(risk.serviceFriction, strings);
  const anonymity = anonymityProfileLabel(risk.anonymityProfile, strings);

  // A pre-risk-v4.12 share snapshot carries none of these — render nothing rather than an empty shell.
  if (!reason && !frictionBody && !anonymity) return null;

  return (
    <div className="risk-why-panel">
      {reason ? (
        <div className="risk-why-panel__section">
          <strong>{strings.riskWhyHeading ?? "Why this score"}</strong>
          <p>{reason}</p>
        </div>
      ) : null}
      {anonymity ? (
        <div className="field field--status">
          <span className="field-label">{strings.anonymityTypeFieldLabel ?? "Anonymity type"}</span>
          <span className="field-value">{anonymity}</span>
        </div>
      ) : null}
      {frictionBody ? (
        <div className="risk-why-panel__section">
          <strong>
            {strings.serviceFrictionHeading ?? "Expected service friction"}
            {frictionLevel ? `: ${frictionLevel}` : ""}
          </strong>
          <p>{frictionBody}</p>
        </div>
      ) : null}
    </div>
  );
}

// Up to 5 ranked facts worth a reader's attention — positive/warning/negative, each mapped from a stable
// server-issued code (see RiskExplanationEngine.BuildKeyFindings). Deliberately not the same list as
// KeyRiskSignals below: this one also carries positive and contextualized/historical findings, not only
// strong risk signals.
export function KeyFindingsList({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  const findings = risk.keyFindings ?? [];
  if (findings.length === 0) return null;

  const heading = strings.keyFindingsHeading ?? "Key findings";

  return (
    <div className="key-findings" aria-label={heading}>
      <ul className="key-findings__list">
        {findings.map((finding, index) => (
          <li key={`${finding.kind}-${finding.code}-${index}`} className={`key-findings__item key-findings__item--${finding.kind}`}>
            <span className="key-findings__dot" aria-hidden="true" />
            <div className="key-findings__text">
              <strong>{keyFindingTitle(finding, strings)}</strong>
              <p>{keyFindingBody(finding, strings)}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// The ranked, curated "what actually drove this score" summary directly under the index — capped at 4
// by the backend already (RiskScoreResult.KeySignals), so this never grows into a long list.
//
// "No strong risk signals detected" is a claim that a real detection ran and came back clean — it may
// only appear when a score actually exists (spec §13). insufficient_data and not_applicable each get
// their own, honest copy and a neutral (not "safe"/green) dot, since neither state is evidence of
// anything being clean.
//
// risk-v4.8's "partial" counts as a score here, and deliberately so: the detections listed below really
// did run and really did come back the way they say. What was incomplete is the *breadth* of the check,
// which DataCoverageNotice states in its own sentence rather than by suppressing findings.
export function KeyRiskSignals({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  const heading = strings.keySignalsHeading ?? "Key signals";

  if (!hasScore(risk.scoreStatus)) {
    const message = risk.scoreStatus === "not_applicable"
      ? (strings.keySignalsNotApplicable ?? "Public IP risk evaluation does not apply to this address.")
      : (strings.keySignalsInsufficientData ?? "Risk evaluation could not be completed.");

    return (
      <div className="key-signals" aria-label={heading}>
        <p className="key-signals__empty key-signals__empty--unavailable">
          <span className="key-signals__dot key-signals__dot--neutral" aria-hidden="true" />
          {message}
        </p>
      </div>
    );
  }

  // The "nothing detected" case is already said once, by KeyFindingsList's own no_signals_detected
  // entry (same underlying condition: no signal in risk.signalStates resolved to Detected) — showing it
  // again here as an unheaded second pill directly beneath that entry reads as a duplicated message
  // rather than a second, distinct section.
  if (risk.keySignals.length === 0) return null;

  return (
    <div className="key-signals" aria-label={heading}>
      <ul className="key-signals__list">
        {risk.keySignals.map((signalType) => {
            // A signal risk-v4.9's RiskExplanationEngine contextualized (e.g. AttackSource on a
            // recognized public DNS resolver) no longer renders as a flat red "strong signal" here —
            // that was the exact confusing-badge problem this feature exists to fix. A result that
            // predates signalExplanations (an old share snapshot) has no entry for this signal type and
            // falls back to the original unconditional red dot.
            const explanation = signalExplanationFor(risk, signalType);
            const tone = explanation ? KEY_SIGNAL_DOT_TONE[severityTone(explanation.severity)] : "alert";
            // risk-v4.10: multiple independent providers confirming the same finding raise confidence, not
            // a second finding — say that explicitly rather than showing a near-duplicate card per source.
            const sourceCount = risk.signalProviderCounts?.[signalType] ?? 0;
            return (
              <li key={signalType} className="key-signals__item">
                <span className={`key-signals__dot key-signals__dot--${tone}`} aria-hidden="true" />
                <span>
                  {signalLabel(signalType, strings)}
                  {sourceCount >= 2 && (
                    <span className="key-signals__confirmation">
                      {" "}
                      {(strings.keySignalConfirmedBySources ?? "Confirmed by {count} independent sources.").replace(
                        "{count}",
                        String(sourceCount),
                      )}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}

// Exit Policy tier -> plain-language sentence (spec §XIX: never show raw "accept 1-65535"/"reject *" as
// the headline — that's what exitPolicySummaryDetail's <details> disclosure below is for). Falls back to
// the Broad copy for an unrecognized/未知 tier, matching TorRiskConstraintDefaults.Unknown's own
// conservative-default reasoning on the scoring side.
function torExitPolicyBody(level: string | null, strings: Strings): string {
  switch (level) {
    case "strict":
      return strings.strongSignalTorPolicyStrict ?? "This Tor exit only permits a limited set of destination ports, primarily web traffic.";
    case "moderate":
      return strings.strongSignalTorPolicyModerate ?? "This Tor exit permits several common network services beyond plain web traffic.";
    case "very_broad":
      return strings.strongSignalTorPolicyVeryBroad ?? "This Tor exit permits nearly the entire range of destination ports, substantially increasing its potential abuse surface.";
    case "broad":
    default:
      return strings.strongSignalTorPolicyBroad ?? "This Tor exit permits a broad range of destination ports, increasing its potential abuse surface.";
  }
}

// A small, non-banner card shown only when a Tor exit node or open proxy is Detected — deliberately not
// a full-width warning banner (spec: keep NetRiskScan's plain, tool-like visual weight even for strong
// signals). The Tor card's Exit Policy line/detail is additional context for the *same* signal, never a
// second score (spec §XVIII/§XX).
export function StrongSignalCallout({
  risk,
  torNetwork,
  strings,
}: {
  risk: PublicRisk;
  torNetwork?: PublicTorNetwork | null;
  strings: Strings;
}) {
  const torDetected = risk.signalStates.Tor === "detected";
  const openProxyDetected = risk.signalStates.OpenProxy === "detected";
  // tor-classification-v1 (spec §XIX): a Tor Middle/Guard relay is a Tor node that cannot carry exit
  // traffic. It gets its own neutral note naming the role, and never the "Tor Exit Node — Detected"
  // card — which is what every Tor relay used to be shown, because the sync classified them all as
  // exits. The Tor signal itself stays exit-only, so this branch is the only thing a non-exit relay
  // can reach.
  const torRelayOnly = torNetwork?.isRelay === true && torNetwork.isExit === false;

  if (!torDetected && !openProxyDetected && !torRelayOnly) return null;

  const exitPolicyLevel = torNetwork?.isExit ? torNetwork.exitPolicyLevel : null;
  const exitPolicyDetail = torNetwork?.isExit ? torNetwork.exitPolicySummaryDetail : null;

  return (
    <div className="strong-signal-callouts">
      {torDetected && (
        <div className="strong-signal-callout">
          <strong>{strings.strongSignalTorTitle ?? "Tor Exit Node — Detected"}</strong>
          <p>{strings.strongSignalTorBody ?? "This IP is currently identified as a Tor exit node."}</p>
          {exitPolicyLevel && <p>{torExitPolicyBody(exitPolicyLevel, strings)}</p>}
          {torNetwork?.isBadExit && (
            <p>{strings.strongSignalTorBadExitBody ?? "The Tor directory authorities have flagged this exit as BadExit."}</p>
          )}
          {exitPolicyDetail && (
            <details className="strong-signal-callout__detail">
              <summary>{strings.strongSignalTorPolicyDetailToggle ?? "View exit policy detail"}</summary>
              <p>{exitPolicyDetail}</p>
            </details>
          )}
        </div>
      )}
      {torRelayOnly && (
        <div className="strong-signal-callout strong-signal-callout--info">
          <strong>{strings.strongSignalTorRelayTitle ?? "Tor Relay — Detected"}</strong>
          <p>{torRelayRoleBody(torNetwork!.role, strings)}</p>
        </div>
      )}
      {openProxyDetected && (
        <div className="strong-signal-callout">
          <strong>{strings.strongSignalOpenProxyTitle ?? "Open Proxy — Detected"}</strong>
          <p>{strings.strongSignalOpenProxyBody ?? "This IP is currently identified as an open proxy."}</p>
        </div>
      )}
    </div>
  );
}

// The role line for a non-exit Tor relay. Says what the relay does and, just as importantly, what it
// does not do — a Guard or Middle relay never originates traffic toward the destination internet, so
// seeing this address in a log is not the same finding as seeing a Tor exit.
function torRelayRoleBody(role: string, strings: Strings): string {
  if (role === "guard")
    return (
      strings.strongSignalTorRelayGuardBody ??
      "This IP is a Tor guard (entry) relay. It relays traffic inside the Tor network and does not carry exit traffic to the public internet."
    );
  return (
    strings.strongSignalTorRelayMiddleBody ??
    "This IP is a Tor middle relay. It relays traffic inside the Tor network and does not carry exit traffic to the public internet."
  );
}

// Collapsed-by-default disclosure grouping the same curated signals into Strong signals / Reputation /
// Network type / No evidence detected — plain-language only, no provider names, no raw scores, no rule
// codes (spec: a visitor should never have to understand the scoring algorithm to see why).
export function WhyThisScore({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  const [open, setOpen] = useState(false);

  // Nothing to disclose when there is no score at all — the fallback "no single signal explains this
  // score" copy below presumes a real score exists, which would misstate insufficient_data/
  // not_applicable as "scored, just not by one standout signal" (spec §13).
  if (!hasScore(risk.scoreStatus)) return null;

  const { strongSignals, contextualizedSignals, identitySignals, reputationSignals, networkSignals, noEvidenceSignals } =
    groupSignalsForWhyThisScore(risk);
  // None of the curated signal types explain this result — most often because the index moved only
  // from a provider's continuous reputation score (e.g. a fraud-risk number), which has no discrete
  // signal to list here. Without this, the disclosure opens onto a blank body, which reads as "clicking
  // does nothing" (this is exactly what a clean-looking IP with a slightly-below-100 index used to do).
  const hasAnyGroup =
    strongSignals.length > 0 ||
    contextualizedSignals.length > 0 ||
    identitySignals.length > 0 ||
    reputationSignals.length > 0 ||
    networkSignals.length > 0 ||
    noEvidenceSignals.length > 0;

  return (
    <details className="why-this-score" open={open} onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}>
      <summary>{strings.whyThisScoreToggle ?? "Why this score?"}</summary>
      <div className="why-this-score__body">
        {strongSignals.length > 0 && (
          <WhyThisScoreGroup title={strings.whyThisScoreGroupStrong ?? "Strong signals"} signalTypes={strongSignals} strings={strings} />
        )}
        {contextualizedSignals.length > 0 && (
          <WhyThisScoreGroup
            title={strings.whyThisScoreGroupContext ?? "Historical / contextualized"}
            signalTypes={contextualizedSignals}
            strings={strings}
          />
        )}
        {identitySignals.length > 0 && (
          <WhyThisScoreGroup
            title={strings.whyThisScoreGroupIdentity ?? "Anonymous network type"}
            signalTypes={identitySignals}
            strings={strings}
          />
        )}
        {reputationSignals.length > 0 && (
          <WhyThisScoreGroup title={strings.whyThisScoreGroupReputation ?? "Reputation"} signalTypes={reputationSignals} strings={strings} />
        )}
        {networkSignals.length > 0 && (
          <WhyThisScoreGroup title={strings.whyThisScoreGroupNetwork ?? "Network type"} signalTypes={networkSignals} strings={strings} />
        )}
        {noEvidenceSignals.length > 0 && (
          <WhyThisScoreGroup title={strings.whyThisScoreGroupClean ?? "No evidence detected"} signalTypes={noEvidenceSignals} strings={strings} />
        )}
        {!hasAnyGroup && (
          <p className="why-this-score__empty">
            {strings.whyThisScoreNoIndividualSignal ??
              "No single signal explains this score on its own — it reflects the overall combination of provider data, including factors too minor to call out individually."}
          </p>
        )}
      </div>
    </details>
  );
}

function WhyThisScoreGroup({ title, signalTypes, strings }: { title: string; signalTypes: string[]; strings: Strings }) {
  return (
    <div className="why-this-score__group">
      <h4>{title}</h4>
      <ul>
        {signalTypes.map((type) => (
          <li key={type}>{signalLabel(type, strings)}</li>
        ))}
      </ul>
    </div>
  );
}

// One plain sentence, shown only when this round's provider data was incomplete — never a percentage,
// never framed as a confidence score (spec: coverage is descriptive, not a second verdict). Only
// meaningful for a "success" result (spec §7 status B: some supplementary sources were unavailable but
// a real score still exists) — insufficient_data/not_applicable already show their own, stronger
// explanation via KeyRiskSignals, so this would just be a confusing second, redundant notice there.
// evidence-quality-v1: "high"/"medium"/"low"/"insufficient"/"unknown" -> a localized label, falling
// back to the raw value itself so an unrecognised future band (spec: treat the set as open) still
// renders something rather than disappearing. Exported so HomeVisitorIpCard's compact footer line can
// use the exact same label vocabulary as this panel rather than a second, drifting copy of it.
export function evidenceLevelLabel(value: string | undefined, strings: Strings): string {
  switch (value) {
    case "high":
      return strings.evidenceLevelHigh ?? "High";
    case "medium":
      return strings.evidenceLevelMedium ?? "Medium";
    case "low":
      return strings.evidenceLevelLow ?? "Low";
    case "insufficient":
      return strings.evidenceLevelInsufficient ?? "Insufficient";
    case "unknown":
    case undefined:
      return strings.evidenceLevelUnknown ?? "Unknown";
    default:
      return value;
  }
}

// Coarse, compact duration text ("18 min", "3 h", "2 d") — deliberately not a full localized sentence
// ("18 minutes ago"): this sits in a label/value row beside "Coverage 87%", and matching that register
// keeps it from reading as a bigger claim than the other three rows.
function formatFreshness(seconds: number): string {
  if (seconds < 60) return "<1 min";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

// evidence-quality-v1: "how much to trust the Index" — Coverage/Agreement/Confidence/Freshness, read
// straight off the paid-API-equivalent fields NetRiskScan already computes internally. Four short
// label/value rows, deliberately never a ring, bar or percentage meter: this panel exists precisely
// because a second visual score next to the ScoreRing is the mistake PublicRiskDto's own design already
// guards against (see its class doc) — the same discipline applies to a new panel introduced beside it.
//
// Absent entirely — not a blank panel — on a pre-evidence-quality-v1 snapshot (risk.evidenceConfidence
// undefined), so an old permalink or cached result keeps rendering exactly as it did before this feature
// existed.
export function EvidenceQualityPanel({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  if (!hasScore(risk.scoreStatus) || !risk.evidenceConfidence) return null;

  const freshness =
    risk.evidenceFreshnessSeconds == null ? null : formatFreshness(risk.evidenceFreshnessSeconds);

  return (
    <div className="evidence-quality-panel">
      <strong>{strings.evidenceQualityTitle ?? "Evidence quality"}</strong>
      <dl>
        <div>
          <dt>{strings.evidenceQualityCoverage ?? "Coverage"}</dt>
          <dd>{risk.coveragePercent}%</dd>
        </div>
        <div>
          <dt>{strings.evidenceQualityAgreement ?? "Agreement"}</dt>
          <dd>{evidenceLevelLabel(risk.evidenceAgreement, strings)}</dd>
        </div>
        <div>
          <dt>{strings.evidenceQualityConfidence ?? "Confidence"}</dt>
          <dd>{evidenceLevelLabel(risk.evidenceConfidence, strings)}</dd>
        </div>
        {freshness && (
          <div>
            <dt>{strings.evidenceQualityFreshness ?? "Freshness"}</dt>
            <dd>{freshness}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

export function DataCoverageNotice({ risk, strings }: { risk: PublicRisk; strings: Strings }) {
  if (!hasScore(risk.scoreStatus)) return null;

  // risk-v4.8 compatibility: when Excellent certification was withheld for want of reputation/attack-
  // history evidence, explain that separately. The visible numeric band remains Excellent; this notice
  // explains why the legacy certification field may still read Good.
  if (risk.excellentEligible === false && (risk.keySignals?.length ?? 0) === 0) {
    return (
      <p className="data-coverage-notice">
        {strings.excellentWithheldNotice ??
          "No risk signals were found for this address, but its reputation and attack history could not be fully checked this time — so this result is not certified as a top-tier clean record."}
      </p>
    );
  }

  // risk-v4.2: when the abuse evidence was thin, say *that* rather than the generic availability
  // sentence. The two are different facts and the stronger one has to win.
  //
  // The wording changed with v4.2 and the change is the point. These sentences used to explain why the
  // index had been held down ("cannot receive a top cleanliness score") — an explanation that is no
  // longer true, because the index is no longer held down. They now say what was and was not checked,
  // which is the only thing thin coverage ever actually established. A high index beside this notice is
  // consistent: nothing was found, and the search did not cover everything.
  const quality = risk.assessmentGrade ?? risk.assessmentQuality;
  if (quality === "insufficient" || quality === "limited" || quality === "partial") {
    const fallback =
      quality === "partial"
        ? "Part of the reputation and attack-history evidence was unavailable for this address, so this check is less thorough than usual."
        : quality === "limited"
          ? "Only part of this address's reputation and attack history could be checked. Nothing in the data we did obtain indicates risk, but the check was not thorough."
          : "Reputation and attack-history data were unavailable for this address. Nothing in the data we did obtain indicates risk, but nothing here confirms a clean record either.";

    const notice =
      quality === "partial"
        ? strings.assessmentPartialNotice
        : quality === "limited"
          ? strings.assessmentLimitedNotice
          : strings.assessmentInsufficientNotice;

    return <p className="data-coverage-notice">{notice ?? fallback}</p>;
  }

  if (risk.dataCoverage === "complete") return null;

  return (
    <p className="data-coverage-notice">
      {strings.dataCoverageNotice ?? "Some data sources were unavailable during this check. The score was calculated using available signals only."}
    </p>
  );
}
